import { useEffect, useRef, useState } from 'react'

import { useDragWithClick } from '@shared'
import { InventoryCard, useGetDropZones } from '@store'

type Props = {
  children: React.ReactNode
  card: InventoryCard
  handleDrop: (card: InventoryCard) => void
  onClick: () => void
  onDragStart: () => void
  onDragEnd: () => void
}

const dragThreshold = 5

type Coordinates = {
  x: number
  y: number
}

export const DragItem = ({
  children,
  card,
  handleDrop,
  onClick,
  onDragStart,
  onDragEnd,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const touchStartPositionRef = useRef<Coordinates | null>(null)
  const [dragPosition, setDragPosition] = useState<Coordinates | null>(null)

  const [isReturnAnimating, setIsReturnAnimating] = useState(false)
  const dropZones = useGetDropZones([card.title || ''])
  const isDirectionDetectedRef = useRef(false)

  const { onPointerDown, onPointerUp, onPointerMove } = useDragWithClick({
    onClick: () => onClick(),
  })

  useEffect(() => {
    if (dragPosition) return onDragStart()
    onDragEnd()
  }, [dragPosition])

  const getContainerRect = () => {
    if (!containerRef.current) return { left: 0, top: 0, right: 0, bottom: 0 }
    return containerRef.current.getBoundingClientRect()
  }

  const isDraggingOrAnimating = dragPosition || isReturnAnimating

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartPositionRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartPositionRef.current) return

    const deltaX = e.touches[0].clientX - touchStartPositionRef.current.x
    const deltaY = e.touches[0].clientY - touchStartPositionRef.current.y

    if (dragPosition) {
      // e.preventDefault()
      e.stopPropagation()
      setDragPosition({
        x: deltaX,
        y: deltaY,
      })
      return
    }

    if (isDirectionDetectedRef.current) return

    if (
      Math.abs(deltaY) > dragThreshold &&
      Math.abs(deltaY) * 1.5 > Math.abs(deltaX)
    ) {
      setDragPosition({
        x: deltaX,
        y: deltaY,
      })
    }
    isDirectionDetectedRef.current = true
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchStartPositionRef.current = null
    isDirectionDetectedRef.current = false
    setDragPosition(null)

    if (dragPosition) {
      setIsReturnAnimating(true)
    }

    const touchX = e.changedTouches[0].clientX
    const touchY = e.changedTouches[0].clientY
    const dropZone = dropZones.find(
      ({ rect }) =>
        touchX >= rect.left &&
        touchX <= rect.right &&
        touchY >= rect.top &&
        touchY <= rect.bottom
    )

    if (dropZone) {
      onDragEnd()
      dropZone.onDropItem(card)
      handleDrop(card)
      return
    }
  }

  return (
    <div ref={containerRef}>
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerMove={onPointerMove}
        onTransitionEnd={() => setIsReturnAnimating(false)}
        style={{
          position: isDraggingOrAnimating ? 'fixed' : 'relative',
          top: isDraggingOrAnimating ? getContainerRect().top : undefined,
          left: isDraggingOrAnimating ? getContainerRect().left : undefined,
          right: isDraggingOrAnimating ? getContainerRect().right : undefined,
          bottom: isDraggingOrAnimating ? getContainerRect().bottom : undefined,
          willChange: 'transform',
          touchAction: dragPosition ? 'none' : 'auto',
          zIndex: isDraggingOrAnimating ? 2 : 1,
          transform: dragPosition
            ? `translate(${dragPosition.x}px, ${dragPosition.y}px)`
            : undefined,
          transition: dragPosition ? undefined : '0.2s',
        }}
      >
        {children}
      </div>
    </div>
  )
}
